import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Video, X } from "lucide-react";
import { Modal } from "../../../ui/components/Modal";
import { Callout } from "../../../ui/components/Callout";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Avatar } from "../../../ui/components/Avatar";
import {
  ListViewUserCertificationCard,
  type ListViewUserCertificationCardModule,
  type ListViewUserCertificationCardStatus,
  type CertificationTrainingMilestone,
} from "../../../ui/components/ListView/components/ListViewUserCertificationCard";
import { shortDate } from "../../../ui/components/ListView/components/DeckCard/logic/formatters";
import { useI18n } from "../../lib/i18n";
import {
  fetchCertificationModules,
  previewProgressClaimMigration,
  type LegacyCertModuleProgress,
  type LegacyCertTrainingProgress,
  type MigrationPreviewModule,
  type MigrationPreviewResult,
} from "../../lib/api/certifications";
import { getLegacyModuleDefinition } from "../../lib/certification/legacy-module-catalog";
import { JsonInspectorModal } from "../common/JsonInspectorModal";
import * as styles from "./MigrationPreviewModal.style.css";

function normalizeStatus(
  status: string | null | undefined,
): ListViewUserCertificationCardStatus {
  switch (status) {
    case "complete":
    case "in_progress":
    case "retake_needed":
    case "not_started":
      return status;
    default:
      return "not_started";
  }
}

function toProjectedCard(
  module: MigrationPreviewModule,
  catalog?: { heroImageUrl?: string | null; trophyImageUrl?: string | null } | null,
): ListViewUserCertificationCardModule {
  const trainings: CertificationTrainingMilestone[] = module.trainings
    .filter((tr) => tr.mappingStatus === "mapped")
    .map((tr) => ({
      id:
        tr.mappedTrainingId ??
        `legacy-step-${module.moduleSlug}-${tr.stepNumber}`,
      title: tr.mappedTrainingTitle ?? `Step ${tr.stepNumber}`,
      section: tr.section ?? undefined,
      completed: tr.passed,
      stepNumber: tr.stepNumber,
      videoUrl: tr.videoUrl ?? null,
      thumbnailUrl: tr.thumbnailUrl ?? null,
      videoDurationSeconds: tr.videoDurationSeconds ?? null,
      userProgress:
        tr.attemptedAt || tr.totalCount > 0
          ? {
              passed: tr.passed,
              score: tr.score,
              correctCount: tr.correctCount,
              totalCount: tr.totalCount,
              attemptedAt: tr.attemptedAt ?? new Date(0).toISOString(),
            }
          : null,
    }));

  const migrationNotice =
    module.carryover === "full_credit" || module.carryover === "partial_reset"
      ? module.carryover
      : null;

  return {
    moduleId: module.mappedModuleId ?? `unmapped:${module.moduleSlug}`,
    moduleTitle: module.mappedModuleTitle ?? module.moduleSlug,
    progressPercent: module.projectedProgressPercent ?? 0,
    completedAt: module.completedAt,
    status: normalizeStatus(module.projectedStatus),
    expiresAt: null,
    heroImageUrl: module.heroImageUrl ?? catalog?.heroImageUrl ?? null,
    trophyImageUrl: module.trophyImageUrl ?? catalog?.trophyImageUrl ?? null,
    trainings,
    migrationNotice,
  };
}

function formatTrainingScore(training: LegacyCertTrainingProgress): string {
  return `${training.correctCount}/${training.totalCount} - ${training.score}%`;
}

function emptyLegacyTraining(
  stepNumber: number,
  questionCount: number,
): LegacyCertTrainingProgress {
  return {
    stepNumber,
    passed: false,
    score: 0,
    correctCount: 0,
    totalCount: Math.max(0, questionCount),
    videoWatched: false,
  };
}

/** Merge claim attempts onto the full legacy curriculum so missing steps show as 0%. */
function expandLegacyTrainings(
  module: LegacyCertModuleProgress,
): LegacyCertTrainingProgress[] {
  const byStep = new Map(
    (module.trainings ?? []).map((tr) => [tr.stepNumber, tr]),
  );
  const catalog = getLegacyModuleDefinition(module.moduleSlug);
  if (!catalog) {
    return [...(module.trainings ?? [])].sort(
      (a, b) => a.stepNumber - b.stepNumber,
    );
  }

  return catalog.steps.map((step) => {
    const existing = byStep.get(step.stepNumber);
    if (!existing) {
      return emptyLegacyTraining(step.stepNumber, step.questionCount);
    }
    return {
      ...existing,
      // Prefer catalog question totals when claim data is incomplete / outdated.
      totalCount:
        existing.totalCount > 0 ? existing.totalCount : step.questionCount,
    };
  });
}

function LegacyTrainingRow({
  training,
}: {
  training: LegacyCertTrainingProgress;
}) {
  const { t } = useI18n();
  const videoWatched = Boolean(training.videoWatched);
  const scoreBadgeClass = [
    styles.legacyScoreBadge,
    training.passed ? styles.legacyScoreBadgePass : styles.legacyScoreBadgeFail,
  ].join(" ");

  return (
    <li className={styles.legacyTrainingRow}>
      <span className={styles.legacyTrainingStep}>{training.stepNumber}.</span>
      <span
        className={scoreBadgeClass}
        title={
          training.passed
            ? t("pro.system.certification.claims.previewPassed")
            : t("pro.system.certification.claims.previewFailed")
        }
      >
        <span
          className={training.passed ? styles.iconPass : styles.iconFail}
          aria-hidden
        >
          {training.passed ? (
            <Check size={11} strokeWidth={3} />
          ) : (
            <X size={11} strokeWidth={3} />
          )}
        </span>
        {formatTrainingScore(training)}
      </span>
      {videoWatched ? (
        <span
          className={styles.legacyVideoBadge}
          title={t("pro.system.certification.claims.previewVideoWatched")}
          aria-label={t("pro.system.certification.claims.previewVideoWatched")}
        >
          <Video size={11} strokeWidth={2.25} aria-hidden />
        </span>
      ) : null}
    </li>
  );
}

function LegacyModuleCard({
  module,
  unmapped,
  legacyTotalSteps,
}: {
  module: LegacyCertModuleProgress;
  unmapped: boolean;
  legacyTotalSteps?: number | null;
}) {
  const { t } = useI18n();
  const catalog = getLegacyModuleDefinition(module.moduleSlug);
  const trainings = expandLegacyTrainings(module);
  const passedCount = trainings.filter((tr) => tr.passed).length;
  const totalCount =
    legacyTotalSteps && legacyTotalSteps > 0
      ? legacyTotalSteps
      : catalog?.totalSteps ?? trainings.length;
  const allComplete = totalCount > 0 && passedCount === totalCount;
  const completeTooltipParts = [
    t("pro.system.certification.claims.previewModuleComplete"),
    module.certNum
      ? t("pro.system.certification.claims.previewCertNum", {
          num: module.certNum,
        })
      : null,
    module.completedAt
      ? t("pro.system.certification.claims.previewCompleted", {
          date: shortDate(module.completedAt),
        })
      : null,
  ].filter(Boolean) as string[];
  const completeTooltip = completeTooltipParts.join(" · ");

  return (
    <div className={styles.legacyModuleCard}>
      <div className={styles.legacyModuleHeader}>
        <div className={styles.legacyModuleTitleStack}>
          <p className={styles.legacyModuleSlug}>{module.moduleSlug}</p>
          <p className={styles.legacyModuleMeta}>
            {t("pro.system.certification.claims.previewSections", {
              passed: String(passedCount),
              total: String(totalCount),
            })}
          </p>
        </div>
        {allComplete ? (
          <span
            className={styles.legacyCompleteBadge}
            title={completeTooltip}
            aria-label={completeTooltip}
          >
            <Check size={12} strokeWidth={3} aria-hidden />
          </span>
        ) : null}
      </div>

      {trainings.length > 0 ? (
        <ul className={styles.legacyTrainingList}>
          {trainings.map((training) => (
            <LegacyTrainingRow
              key={training.stepNumber}
              training={training}
            />
          ))}
        </ul>
      ) : (
        <p className={styles.legacyModuleMeta}>
          {t("pro.system.certification.claims.previewNoTrainings")}
        </p>
      )}

      {unmapped ? (
        <span className={styles.legacyModuleBadge}>
          {t("pro.system.certification.claims.previewUnmappedModule")}
        </span>
      ) : null}
    </div>
  );
}

function SectionHeading({
  title,
  onOpenJson,
}: {
  title: string;
  onOpenJson?: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className={styles.sectionHeader}>
      <h4 className={styles.sectionTitle}>{title}</h4>
      {onOpenJson ? (
        <button
          type="button"
          className={styles.jsonButton}
          onClick={onOpenJson}
          aria-label={t("pro.system.certification.claims.previewViewJson")}
          title={t("pro.system.certification.claims.previewViewJson")}
        >
          {"{}"}
        </button>
      ) : null}
    </div>
  );
}

export function MigrationPreviewModal({
  open,
  setOpen,
  claimId,
  accessToken,
  showJsonInspectors = true,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  claimId: string | null;
  accessToken: string | null;
  /** Admin claims page only — hide raw JSON controls for invite preview. */
  showJsonInspectors?: boolean;
}) {
  const { t } = useI18n();
  const [jsonView, setJsonView] = React.useState<{
    title: string;
    value: unknown;
  } | null>(null);

  const previewQuery = useQuery({
    queryKey: ["certification-progress-claim-preview", claimId],
    queryFn: (): Promise<MigrationPreviewResult> =>
      previewProgressClaimMigration(accessToken!, claimId!),
    enabled: Boolean(open && claimId && accessToken),
    staleTime: 0,
  });

  const modulesCatalogQuery = useQuery({
    queryKey: ["certification-modules", "migration-preview-enrich"],
    queryFn: () => fetchCertificationModules(accessToken!),
    enabled: Boolean(open && accessToken),
    staleTime: 60_000,
  });

  const preview = previewQuery.data;
  const error = previewQuery.error
    ? (previewQuery.error as Error).message ||
      t("pro.system.certification.claims.previewError")
    : null;

  const modulesById = React.useMemo(() => {
    const map = new Map<
      string,
      { heroImageUrl?: string | null; trophyImageUrl?: string | null }
    >();
    for (const mod of modulesCatalogQuery.data ?? []) {
      map.set(mod.id, {
        heroImageUrl: mod.heroImageUrl,
        trophyImageUrl: mod.trophyImageUrl,
      });
    }
    return map;
  }, [modulesCatalogQuery.data]);

  const claimMeta = React.useMemo(() => {
    if (!preview) return null;
    const parts: string[] = [];
    if (preview.existingUser) {
      const name = [preview.existingUser.firstName, preview.existingUser.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      parts.push(name || preview.existingUser.email);
    } else {
      parts.push(t("pro.system.certification.claims.previewNoUser"));
    }
    if (preview.unmappedTrainings > 0) {
      parts.push(
        t("pro.system.certification.claims.previewUnmapped", {
          count: String(preview.unmappedTrainings),
        }),
      );
    }
    return parts.join(" · ");
  }, [preview, t]);

  const projectedCards = React.useMemo(
    () =>
      (preview?.modules ?? [])
        .filter((module) => module.mappingStatus === "mapped")
        .map((module) =>
          toProjectedCard(
            module,
            module.mappedModuleId
              ? modulesById.get(module.mappedModuleId)
              : null,
          ),
        ),
    [preview, modulesById],
  );

  React.useEffect(() => {
    if (!open) setJsonView(null);
  }, [open]);

  return (
    <>
      <Modal
        open={open}
        setOpen={setOpen}
        minWidth="640px"
        maxWidth="920px"
        minHeight="auto"
        maxHeight="90vh"
        closeOnClickOff
      >
        <Modal.Header>
          <Text level="h3" weight="bold">
            {t("pro.system.certification.claims.previewTitle")}
          </Text>
        </Modal.Header>
        <Modal.Body>
          <div className={styles.body}>
            {error && <Callout variant="error">{error}</Callout>}
            {previewQuery.isLoading && (
              <Text level="body-small" color="muted">
                {t("shared.common.loading")}
              </Text>
            )}
            {preview && (
              <>
                <section className={styles.section}>
                  <SectionHeading
                    title={t("pro.system.certification.claims.previewClaimSection")}
                    onOpenJson={
                      showJsonInspectors
                        ? () =>
                            setJsonView({
                              title: t(
                                "pro.system.certification.claims.previewClaimJsonTitle",
                              ),
                              value: preview.claim,
                            })
                        : undefined
                    }
                  />
                  <div className={styles.claimCard}>
                    <div className={styles.claimHeader}>
                      <Avatar
                        email={preview.claim.email}
                        firstName={preview.claim.email}
                        lastName=""
                        size={44}
                      />
                      <div className={styles.claimIdentity}>
                        <p className={styles.claimEmail}>{preview.claim.email}</p>
                        {claimMeta ? (
                          <p className={styles.claimMeta}>{claimMeta}</p>
                        ) : null}
                      </div>
                    </div>
                    {preview.claim.payload.modules.length > 0 ? (
                      <>
                        <p className={styles.coursesCount}>
                          {t("pro.system.certification.claims.previewCoursesCount", {
                            count: preview.claim.payload.modules.length,
                          })}
                        </p>
                        <div className={styles.moduleStrip}>
                          {preview.claim.payload.modules.map((module) => {
                            const projected = preview.modules.find(
                              (m) => m.moduleSlug === module.moduleSlug,
                            );
                            return (
                              <LegacyModuleCard
                                key={module.moduleSlug}
                                module={module}
                                unmapped={
                                  projected?.mappingStatus === "unmapped_module"
                                }
                                legacyTotalSteps={projected?.legacyTotalSteps}
                              />
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <p className={styles.emptyText}>
                        {t("pro.system.certification.claims.previewEmptyModules")}
                      </p>
                    )}
                  </div>
                </section>

                <section className={styles.section}>
                  <SectionHeading
                    title={t(
                      "pro.system.certification.claims.previewResultSection",
                    )}
                    onOpenJson={
                      showJsonInspectors
                        ? () =>
                            setJsonView({
                              title: t(
                                "pro.system.certification.claims.previewResultJsonTitle",
                              ),
                              value: {
                                applyPath: preview.applyPath,
                                alreadyClaimed: preview.alreadyClaimed,
                                existingUser: preview.existingUser,
                                unmappedTrainings: preview.unmappedTrainings,
                                modules: preview.modules,
                              },
                            })
                        : undefined
                    }
                  />
                  {projectedCards.length > 0 ? (
                    <div className={styles.projectedStack}>
                      {projectedCards.map((cert) => (
                        <ListViewUserCertificationCard
                          key={cert.moduleId}
                          layout="stack"
                          certification={cert}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className={styles.emptyText}>
                      {t(
                        "pro.system.certification.claims.previewEmptyProjected",
                      )}
                    </p>
                  )}
                </section>
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: "flex-end" }}>
          <Button
            color="neutral"
            variant="outline"
            size="md"
            onClick={() => setOpen(false)}
          >
            {t("shared.common.cancel")}
          </Button>
        </Modal.Footer>
      </Modal>

      <JsonInspectorModal
        open={Boolean(jsonView)}
        setOpen={(next) => {
          if (!next) setJsonView(null);
        }}
        title={jsonView?.title ?? ""}
        value={jsonView?.value}
      />
    </>
  );
}
